<template>
  <div>
    <!-- Hero -->
    <div class="position-relative">
      <section class="section-shaped my-0">
        <div class="shape shape-skew" style="background-color: #0066CC">
          <span></span><span></span><span></span><span></span>
          <span></span><span></span><span></span><span></span><span></span>
        </div>
        <div class="container shape-container d-flex">
          <div class="col px-0">
            <div class="row">
              <div class="col-lg-6">
                <h1 class="display-3 text-white">Blog</h1>
                <p class="lead text-white">
                  News, updates, and stories from D.Y.D. Records.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Published Posts Section -->
    <section class="section section-lg">
      <div class="container">
        <!-- Filter Bar -->
        <div class="row mb-4 align-items-center">
          <div class="col-lg-6">
            <h2 class="display-4 mb-0">Latest Posts</h2>
          </div>
          <div class="col-lg-6 text-lg-right mt-3 mt-lg-0">
            <div class="d-inline-flex align-items-center">
              <input
                type="text"
                class="form-control form-control-sm mr-2"
                placeholder="Search posts..."
                v-model="searchQuery"
                style="max-width: 220px;"
              />
              <select
                class="form-control form-control-sm"
                v-model="selectedTag"
                style="max-width: 160px;"
              >
                <option value="">All Tags</option>
                <option v-for="tag in allTags" :key="tag" :value="tag">{{ tag }}</option>
              </select>
            </div>
          </div>
        </div>

        <blog-list :posts="filteredPosts"></blog-list>
      </div>
    </section>

    <!-- Password Modal -->
    <div v-if="showPasswordModal" class="modal d-block" style="background-color: rgba(0,0,0,0.6);">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-dark border-0">
            <h5 class="modal-title text-white">
              <i class="fa fa-lock mr-2"></i>Blog Editor Access
            </h5>
            <button type="button" class="close text-white" @click="showPasswordModal = false">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body bg-dark">
            <p class="text-white mb-3">Enter password to access the blog editor:</p>
            <input
              type="password"
              class="form-control form-control-lg"
              placeholder="Password"
              v-model="passwordInput"
              @keyup.enter="verifyPassword"
            />
            <small v-if="passwordError" class="text-danger d-block mt-2">{{ passwordError }}</small>
          </div>
          <div class="modal-footer bg-dark border-0">
            <button type="button" class="btn btn-secondary" @click="showPasswordModal = false">Cancel</button>
            <button type="button" class="btn btn-primary" @click="verifyPassword">Unlock</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Access Editor Button Section -->
    <section v-if="!isAuthenticated" class="section section-lg section-shaped overflow-hidden my-0">
      <div class="shape shape-default" style="background: #1a1a2e">
        <span></span><span></span><span></span><span></span>
        <span></span><span></span><span></span><span></span>
      </div>
      <div class="container shape-container py-0 pb-5 text-center">
        <div class="row">
          <div class="col-12">
            <h2 class="display-4 text-white mb-4">
              <i class="fa fa-pencil-square-o mr-2"></i>Blog Management
            </h2>
            <p class="text-white-50 lead mb-4">Access the blog editor to create and manage posts</p>
            <button class="btn btn-success btn-lg" @click="showPasswordModal = true">
              <i class="fa fa-unlock mr-2"></i>Access Editor
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Blog Editor Section -->
    <section v-if="isAuthenticated" class="section section-lg section-shaped overflow-hidden my-0">
      <div class="shape shape-default" style="background: #1a1a2e">
        <span></span><span></span><span></span><span></span>
        <span></span><span></span><span></span><span></span>
      </div>
      <div class="container shape-container py-0 pb-5">
        <div class="row mb-4">
          <div class="col-12 d-flex justify-content-between align-items-center">
            <h2 class="display-4 text-white mb-0">
              <i class="fa fa-pencil-square-o mr-2"></i>
              {{ editingPost ? 'Edit Post' : 'Create New Post' }}
            </h2>
            <button
              v-if="!showEditor"
              class="btn btn-success"
              @click="showEditor = true"
            >
              <i class="fa fa-plus mr-2"></i>New Post
            </button>
            <button
              v-else
              class="btn btn-outline-light"
              @click="cancelEditing"
            >
              <i class="fa fa-times mr-2"></i>Close Editor
            </button>
          </div>
        </div>

        <!-- Editor -->
        <div v-if="showEditor">
          <blog-editor
            :post="editingPost"
            @save="handleSave"
            @cancel="cancelEditing"
          ></blog-editor>
        </div>

        <!-- Manage Existing Posts -->
        <div v-if="!showEditor" class="mt-4">
          <h3 class="text-white mb-3">
            <i class="fa fa-cog mr-2"></i>Manage Posts
            <span class="badge badge-light ml-2">{{ allPosts.length }}</span>
          </h3>
          <blog-list
            :posts="allPosts"
            :show-actions="true"
            @edit="startEditing"
            @delete="handleDelete"
            @toggle-publish="handleTogglePublish"
          ></blog-list>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import BlogEditor from '@/components/BlogEditor.vue'
import BlogList from '@/components/BlogList.vue'

export default {
  name: 'Blog',
  components: {
    BlogEditor,
    BlogList
  },
  data() {
    return {
      showEditor: false,
      editingPost: null,
      searchQuery: '',
      selectedTag: '',
      isAuthenticated: false,
      showPasswordModal: false,
      passwordInput: '',
      passwordError: '',
      CORRECT_PASSWORD: 'jK89.?dwq'
    }
  },
  created() {
    this.$store.dispatch('blog/fetchPosts')
  },
  computed: {
    allPosts() {
      return this.$store.getters['blog/allPosts']
    },
    publishedPosts() {
      return this.$store.getters['blog/publishedPosts']
    },
    allTags() {
      return this.$store.getters['blog/allTags']
    },
    filteredPosts() {
      let posts = this.publishedPosts

      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase()
        posts = posts.filter(p =>
          p.title.toLowerCase().includes(query) ||
          (p.excerpt && p.excerpt.toLowerCase().includes(query)) ||
          (p.author && p.author.toLowerCase().includes(query))
        )
      }

      if (this.selectedTag) {
        posts = posts.filter(p => p.tags && p.tags.includes(this.selectedTag))
      }

      return posts
    }
  },
  methods: {
    verifyPassword() {
      if (this.passwordInput === this.CORRECT_PASSWORD) {
        this.isAuthenticated = true
        this.showPasswordModal = false
        this.passwordInput = ''
        this.passwordError = ''
      } else {
        this.passwordError = 'Incorrect password'
        this.passwordInput = ''
      }
    },
    handleSave(postData) {
      if (this.editingPost && this.editingPost._id) {
        this.$store.dispatch('blog/updatePost', { ...postData, _id: this.editingPost._id })
      } else {
        this.$store.dispatch('blog/addPost', postData)
      }
      this.cancelEditing()
    },
    startEditing(post) {
      this.editingPost = { ...post, tags: [...(post.tags || [])] }
      this.showEditor = true
      window.scrollTo({
        top: document.querySelector('.section-shaped.overflow-hidden').offsetTop - 80,
        behavior: 'smooth'
      })
    },
    cancelEditing() {
      this.editingPost = null
      this.showEditor = false
    },
    handleDelete(postId) {
      this.$store.dispatch('blog/deletePost', postId)
    },
    handleTogglePublish(postId) {
      this.$store.dispatch('blog/togglePublish', postId)
    }
  }
}
</script>

<style scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content {
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.bg-dark {
  background-color: #1a1a2e !important;
}

.text-white {
  color: #ffffff;
}
</style>

<style scoped>
.display-4 {
  font-size: 1.8rem;
  font-weight: 600;
}
</style>
